import React, { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowUpRight, Linkedin, Instagram, Facebook } from "lucide-react";
import Logo from "@/components/Logo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const COLS = [
  {
    title: "Products",
    links: [
      "Stand-up Pouches",
      "Spouted Pouches",
      "Center-seal Bags",
      "3-side Seal Sachets",
      "Flat Bottom Pouches",
      "Custom Die-cut",
    ],
  },
  {
    title: "Industries",
    links: [
      "Food & Beverage",
      "Nutraceuticals",
      "Personal Care",
      "Pet Food",
      "Industrial",
      "Eco / Compostable",
    ],
  },
  {
    title: "Company",
    links: [
      "About Pacific Group",
      "Sustainability",
      "Careers",
      "Press",
      "Contact",
    ],
  },
];

export default function Footer() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);

  const subscribe = async (e) => {
    e.preventDefault();
    if (!email.trim()) return toast.error("Enter your email first.");
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter`, { email });
      toast.success("You're on the list. Welcome to PouchKart.");
      setEmail("");
    } catch {
      toast.error("Couldn't subscribe right now.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <footer
      data-testid="site-footer"
      className="bg-[#0a1128] text-white border-t-4 border-[#F58220]"
    >
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 lg:px-16 py-16">
        {/* Big call line */}
        <div className="grid grid-cols-12 gap-8 pb-12 border-b border-white/10">
          <div className="col-span-12 lg:col-span-7">
            <div className="text-[11px] font-bold uppercase tracking-[0.28em] text-[#F58220] mb-4">
              Newsletter
            </div>
            <h3 className="font-display text-3xl md:text-5xl font-black uppercase leading-[0.95] tracking-tighter">
              Packaging tactics<br />for growing brands.
            </h3>
            <p className="mt-4 text-white/65 max-w-lg">
              One short, useful note every month — film science, print
              breakthroughs, MOQ math, factory news.
            </p>
          </div>
          <form
            onSubmit={subscribe}
            data-testid="newsletter-form"
            className="col-span-12 lg:col-span-5 flex flex-col justify-end"
          >
            <div className="flex">
              <input
                data-testid="newsletter-email"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@brand.com"
                className="flex-1 bg-white/5 border border-white/15 placeholder:text-white/40 text-white px-5 py-4 focus:outline-none focus:border-[#F58220]"
              />
              <button
                type="submit"
                data-testid="newsletter-submit"
                disabled={loading}
                className="inline-flex items-center gap-2 bg-[#F58220] hover:bg-[#d9701a] text-white px-6 py-4 text-[12px] font-bold uppercase tracking-[0.16em] disabled:opacity-60"
              >
                {loading ? "..." : "Subscribe"}
                <ArrowUpRight size={16} />
              </button>
            </div>
            <p className="mt-3 text-[11px] text-white/45 uppercase tracking-[0.16em] font-semibold">
              No spam. Unsubscribe with one click.
            </p>
          </form>
        </div>

        {/* Columns */}
        <div className="grid grid-cols-12 gap-10 pt-12">
          <div className="col-span-12 md:col-span-4">
            <Logo variant="dark" height={120} />
            <p className="mt-5 text-sm text-white/65 max-w-xs leading-relaxed">
              PouchKart is a Pacific Group brand — making world-class flexible
              packaging accessible to every ambitious brand, big or small.
            </p>
            <div className="mt-6 flex items-center gap-3">
              {[
                {
                  Icon: Linkedin,
                  label: "linkedin",
                  href: "https://www.linkedin.com/company/pouchkart",
                },
                { Icon: Instagram, label: "instagram", href: "#" },
                { Icon: Facebook, label: "facebook", href: "#" },
              ].map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target={href.startsWith("http") ? "_blank" : undefined}
                  rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                  data-testid={`social-${label}`}
                  aria-label={label}
                  className="w-10 h-10 inline-flex items-center justify-center border border-white/20 hover:bg-[#F58220] hover:border-[#F58220] transition-colors"
                >
                  <Icon size={16} />
                </a>
              ))}
            </div>
          </div>

          {COLS.map((c) => (
            <div key={c.title} className="col-span-6 md:col-span-2 lg:col-span-2">
              <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#F58220] mb-4">
                {c.title}
              </div>
              <ul className="space-y-3">
                {c.links.map((l) => (
                  <li key={l}>
                    <a
                      href="#"
                      className="text-sm text-white/75 hover:text-white transition-colors"
                    >
                      {l}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="col-span-12 md:col-span-2 lg:col-span-2">
            <div className="text-[10px] font-bold uppercase tracking-[0.24em] text-[#F58220] mb-4">
              Reach us
            </div>
            <ul className="space-y-3 text-sm text-white/75">
              <li>
                <a href="tel:+919327903300" className="hover:text-white">
                  +91 93279 03300
                </a>
              </li>
              <li>
                <a href="mailto:hello@pouchkart.co.in" className="hover:text-white">
                  hello@pouchkart.co.in
                </a>
              </li>
              <li>www.pouchkart.co.in</li>
              <li className="text-white/55">
                Pacific Laminates,<br />Pardi, Gujarat
              </li>
            </ul>
          </div>
        </div>

        {/* Bottom bar */}
        <div className="mt-14 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-3 text-[11px] uppercase tracking-[0.18em] font-semibold text-white/50">
          <div>© {new Date().getFullYear()} PouchKart · Pacific Group. All rights reserved.</div>
          <div className="flex items-center gap-6">
            <a href="#" className="hover:text-white">Privacy</a>
            <a href="#" className="hover:text-white">Terms</a>
            <a href="#" className="hover:text-white">Sustainability</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
